import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui';

/* ── Types ──────────────────────────────────────────────────────── */

export interface NavLink {
  /** Display label */
  label: string;
  /** Route path (Link) or anchor ("#section") */
  to: string;
  /** Mark the link as active (e.g. current page) */
  active?: boolean;
  /** Hide on mobile menu — desktop only */
  desktopOnly?: boolean;
}

export interface PublicNavProps {
  /** Navigation links rendered between the logo and the CTA group.
   *  Default: Features, Pricing, Blog, Support */
  links?: NavLink[];
  /** Primary CTA label. Default: none (hidden) */
  ctaLabel?: string;
  /** Primary CTA route. Default: /register */
  ctaTo?: string;
  /** Show the Log In button. Default: true */
  showLogin?: boolean;
}

/* ── Defaults ───────────────────────────────────────────────────── */

const defaultLinks: NavLink[] = [
  { label: 'Features', to: '/features' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Blog', to: '/blog' },
  { label: 'Support', to: '/support' },
];

/* ── Component ──────────────────────────────────────────────────── */

const PublicNav: React.FC<PublicNavProps> = ({
  links = defaultLinks,
  ctaLabel,
  ctaTo = '/register',
  showLogin = true,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const renderLink = (link: NavLink) => {
    const isAnchor = link.to.startsWith('#');
    const baseClasses = `text-[15px] font-medium no-underline transition-colors whitespace-nowrap max-[900px]:text-[13px] max-[768px]:block max-[768px]:py-3 max-[768px]:px-4 max-[768px]:rounded-lg max-[768px]:whitespace-normal`;
    const colorClasses = link.active
      ? 'text-crimson'
      : 'text-text-secondary hover:text-accent';

    const className = `${baseClasses} ${colorClasses}`;

    if (isAnchor) {
      return (
        <a
          key={link.to}
          href={link.to}
          onClick={closeMobileMenu}
          className={className}
        >
          {link.label}
        </a>
      );
    }

    return (
      <Link
        key={link.to}
        to={link.to}
        onClick={closeMobileMenu}
        className={className}
      >
        {link.label}
      </Link>
    );
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-[var(--theme-bg-elevated)] backdrop-blur-[10px] border-b border-border-light z-[100]" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div className="max-w-[1200px] mx-auto flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4">
        {/* Logo */}
        <Link to="/" className="text-2xl font-bold text-accent no-underline">
          Helm
        </Link>

        {/* Desktop links + actions, doubles as mobile slide-out */}
        <div
          className={`flex items-center gap-6 max-[900px]:gap-3 max-[768px]:hidden max-[768px]:fixed max-[768px]:top-0 max-[768px]:right-0 max-[768px]:w-[280px] max-[768px]:h-screen max-[768px]:bg-[var(--theme-bg-primary)] max-[768px]:flex-col max-[768px]:pt-[calc(80px+env(safe-area-inset-top,0px))] max-[768px]:px-6 max-[768px]:pb-6 max-[768px]:gap-2 max-[768px]:shadow-[-4px_0_20px_var(--theme-shadow)] max-[768px]:z-[200] ${
            mobileMenuOpen ? 'max-[768px]:!flex' : ''
          }`}
        >
          {links.map(renderLink)}

          {showLogin && (
            <Link
              to="/login"
              className="px-5 py-2.5 rounded-lg font-semibold text-sm text-text-primary no-underline hover:bg-surface transition-all max-[900px]:px-3.5 max-[900px]:py-2 max-[900px]:text-[13px] max-[768px]:w-full max-[768px]:text-center"
              onClick={closeMobileMenu}
            >
              Log In
            </Link>
          )}

          {ctaLabel && (
            <Link to={ctaTo} onClick={closeMobileMenu} className="max-[768px]:w-full">
              <Button
                variant="primary"
                size="sm"
                className="bg-gradient-to-r from-crimson to-bronze hover:from-crimson/90 hover:to-bronze/90 max-[768px]:w-full"
              >
                {ctaLabel}
              </Button>
            </Link>
          )}
        </div>

        {/* Hamburger (mobile only) */}
        <button
          className="hidden max-[768px]:block bg-transparent border-0 p-2 cursor-pointer z-[201]"
          onClick={toggleMobileMenu}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          <span className="block w-6 h-0.5 bg-text-primary my-[5px] rounded-sm" />
          <span className="block w-6 h-0.5 bg-text-primary my-[5px] rounded-sm" />
          <span className="block w-6 h-0.5 bg-text-primary my-[5px] rounded-sm" />
        </button>
      </div>
    </nav>
  );
};

export default PublicNav;
